import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/utils'
import { X } from 'lucide-react'
import type { ReactNode } from 'react'

/**
 * Figma CSS (node 23:1001):
 * - Container: rounded-full (9999px), px-2 (8px), py-0.5 (2px), gap-1 (4px), border border-transparent
 * - Text: text-xs (12px), font-medium (500), leading-4 (16px), whitespace-nowrap
 * - Variants: Default, Secondary, Outline, Destructive, Verified, Ghost
 */
const intuiBadgeVariants = cva(
  'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium leading-4 whitespace-nowrap transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
  {
    variants: {
      variant: {
        default: 'border-transparent bg-primary text-primary-foreground hover:bg-primary/80',
        secondary: 'border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80',
        destructive: 'border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80',
        outline: 'text-foreground',
        ghost: 'border-transparent text-foreground hover:bg-accent',
        success: 'border-transparent bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100',
        warning: 'border-transparent bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-100',
      },
    },
    defaultVariants: { variant: 'default' },
  }
)

export interface IntuiBadgeProps extends VariantProps<typeof intuiBadgeVariants> {
  children: ReactNode
  icon?: ReactNode
  removable?: boolean
  onRemove?: () => void
  className?: string
}

function IntuiBadge({ children, variant, icon, removable, onRemove, className }: IntuiBadgeProps) {
  return (
    <div className={cn(intuiBadgeVariants({ variant }), className)}>
      {/* Figma: icon 12px (h-3 w-3) */}
      {icon && <span className="[&>svg]:h-3 [&>svg]:w-3">{icon}</span>}
      {children}
      {removable && (
        <button onClick={onRemove} className="ml-1 -mr-0.5 rounded-full hover:bg-black/10" aria-label="Remove">
          <X className="h-3 w-3" />
        </button>
      )}
    </div>
  )
}

export { IntuiBadge, intuiBadgeVariants }
